import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Building2,
  Sparkles,
  Upload,
  Eye,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Globe,
  Lock,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { API } from "../Sidebar";
import { ImageManagerCard } from "./ImageManagerCard";

const SITE_SLUG_SEED_ENDPOINT = `${API}/commercial/sites/seed-a-star`;

const STATUS_COLORS = {
  pending: "bg-slate-200 text-slate-700",
  partial: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

export const CommercialDashboard = () => {
  const [site, setSite] = useState(null);
  const [plan, setPlan] = useState(null);
  const [stats, setStats] = useState(null);
  const [wpSettings, setWpSettings] = useState(null);
  const [wpStatus, setWpStatus] = useState({ tested: false, ok: false, user: null });
  const [busy, setBusy] = useState({});           // per-batch action lock
  const [showCreds, setShowCreds] = useState(false);
  const [credsDraft, setCredsDraft] = useState({ wp_site_url: "", wp_username: "", wp_app_password: "" });
  const [log, setLog] = useState([]);             // recent action log (in-memory)
  const [dryRun, setDryRun] = useState(false);

  const pushLog = (kind, msg) => setLog((l) => [
    { kind, msg, at: new Date().toLocaleTimeString() },
    ...l,
  ].slice(0, 20));

  const markBusy = (key, value) => setBusy((b) => ({ ...b, [key]: value }));

  // ---------- Bootstrap ----------
  const bootstrap = async () => {
    try {
      const seed = await axios.post(SITE_SLUG_SEED_ENDPOINT);
      const siteId = seed.data.id;
      const [planRes, statsRes, wpRes] = await Promise.all([
        axios.get(`${API}/commercial/sites/${siteId}/publishing-plan`),
        axios.get(`${API}/commercial/sites/${siteId}/stats`),
        axios.get(`${API}/commercial/admin/wp-settings`),
      ]);
      setSite(seed.data);
      setPlan(planRes.data);
      setStats(statsRes.data);
      setWpSettings(wpRes.data);
      setCredsDraft((prev) => ({
        wp_site_url: wpRes.data.wp_site_url || "",
        wp_username: wpRes.data.wp_username || "",
        wp_app_password: prev.wp_app_password || "",
      }));
    } catch (e) {
      toast.error("Échec du chargement du dashboard A-Star");
      console.error(e);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTimeout(bootstrap, 0); }, []);

  // ---------- WP Settings ----------
  const saveWpSettings = async () => {
    markBusy("wp-save", true);
    try {
      const res = await axios.put(`${API}/commercial/admin/wp-settings`, credsDraft);
      setWpSettings(res.data);
      setCredsDraft({ ...credsDraft, wp_app_password: "" });
      toast.success("Credentials WordPress sauvegardés");
      pushLog("ok", "WP credentials saved to admin_settings");
    } catch (e) {
      toast.error("Échec de la sauvegarde");
    } finally { markBusy("wp-save", false); }
  };

  const testWp = async () => {
    if (!site) return;
    markBusy("wp-test", true);
    try {
      const res = await axios.post(
        `${API}/commercial/sites/${site.id}/wp/test-connection`,
        {},
      );
      setWpStatus({ tested: true, ok: !!res.data.success, user: res.data.user });
      if (res.data.success) {
        toast.success(`Connecté en tant que ${res.data.user}`);
        pushLog("ok", `WP connection OK (user: ${res.data.user})`);
      } else {
        toast.error("Connexion WP refusée");
        pushLog("err", "WP connection refused");
      }
    } catch (e) {
      setWpStatus({ tested: true, ok: false, user: null });
      toast.error(e?.response?.data?.detail || "Échec du test de connexion");
      pushLog("err", "WP connection error");
    } finally { markBusy("wp-test", false); }
  };

  // ---------- Batch actions ----------
  const previewBatch = async (n) => {
    markBusy(`prev-${n}`, true);
    try {
      const res = await axios.get(`${API}/commercial/sites/${site.id}/wp/publishable-pages/${n}`);
      pushLog("info", `Preview Batch ${n}: ${res.data.count} pages prêtes`);
      toast.message(`Batch ${n} — ${res.data.count} pages`, {
        description: res.data.pages.slice(0, 6).map((p) => p.path).join("\n") +
                     (res.data.pages.length > 6 ? `\n…+${res.data.pages.length - 6} autres` : ""),
      });
    } catch (e) {
      toast.error("Échec du preview");
    } finally { markBusy(`prev-${n}`, false); }
  };

  const generateAI = async (n) => {
    markBusy(`ai-${n}`, true);
    pushLog("info", `Génération AI Batch ${n} en cours…`);
    try {
      const res = await axios.post(`${API}/commercial/sites/${site.id}/ai/generate-batch/${n}`);
      const generated = res.data.generated || 0;
      const skipped = res.data.skipped || 0;
      const errors = (res.data.errors || []).length;
      toast.success(`AI Batch ${n}: ${generated} générés, ${skipped} skipped, ${errors} erreurs`);
      pushLog(errors ? "warn" : "ok", `AI Batch ${n} — generated:${generated} skipped:${skipped} errors:${errors}`);
      await bootstrap();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
      pushLog("err", `AI Batch ${n} failed`);
    } finally { markBusy(`ai-${n}`, false); }
  };

  const publishBatch = async (n) => {
    if (!dryRun && !window.confirm(
      `Publier le Batch ${n} vers WordPress (live) ?\n\nCela va écraser les pages existantes (idempotent).`
    )) return;
    markBusy(`pub-${n}`, true);
    pushLog("info", `${dryRun ? "Dry-run" : "Publish"} Batch ${n} en cours…`);
    try {
      const url = `${API}/commercial/sites/${site.id}/wp/publish-batch/${n}${dryRun ? "?dry_run=true" : ""}`;
      const res = await axios.post(url);
      const d = res.data;
      if (d.dry_run) {
        toast.success(`Dry-run OK: ${d.total} pages prêtes (aucun push)`);
        pushLog("ok", `Dry-run Batch ${n} — ${d.total} pages prêtes`);
      } else {
        if (d.errors === 0) {
          toast.success(`Batch ${n} publié: ${d.published}/${d.total}`);
          pushLog("ok", `Publish Batch ${n} — ${d.published} ok, ${d.errors} err, front:${d.homepage_set_as_front}`);
        } else {
          toast.warning(`Batch ${n} partiel: ${d.published} ok, ${d.errors} erreurs`);
          pushLog("warn", `Publish Batch ${n} — ${d.published} ok, ${d.errors} err`);
        }
        await bootstrap();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
      pushLog("err", `Publish Batch ${n} failed`);
    } finally { markBusy(`pub-${n}`, false); }
  };

  const activateStates = async (n) => {
    markBusy(`st-${n}`, true);
    try {
      const res = await axios.post(`${API}/commercial/sites/${site.id}/publishing-plan/${n}/activate-states`);
      toast.success(`États activés: ${(res.data.activated || []).join(", ") || "aucun"}`);
      pushLog("ok", `Batch ${n} states activated: ${(res.data.activated || []).join(",")}`);
      await bootstrap();
    } catch {
      toast.error("Échec de l'activation");
    } finally { markBusy(`st-${n}`, false); }
  };

  if (!site || !plan) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="commercial-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const totalPublished = plan.batches.reduce((acc, b) => acc + b.published_count, 0);
  const totalTarget = plan.total_target;
  const progressPct = Math.round((totalPublished / Math.max(totalTarget, 1)) * 100);

  return (
    <div className="space-y-6" data-testid="commercial-dashboard">
      {/* ============== Header ============== */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-5 h-5 text-amber-500" />
            <span className="text-[11px] tracking-wider uppercase text-amber-600 font-semibold">
              Commercial Network
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">A-Star Building Solutions</h1>
          <p className="text-sm text-slate-500">
            {site.domain_name} · {stats?.estimated_page_volume?.total ?? "?"} pages cibles ·
            {" "}{totalPublished} publiées
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`https://${site.domain_name}`}
            target="_blank" rel="noopener noreferrer"
            className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
            data-testid="commercial-visit-site"
          >
            <ExternalLink className="w-4 h-4" /> Voir le site
          </a>
          <Button
            variant="outline" size="sm"
            onClick={bootstrap}
            data-testid="commercial-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* ============== Global progress ============== */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Progression globale</span>
            <span className="text-sm text-slate-500 font-mono">{totalPublished} / {totalTarget} pages ({progressPct}%)</span>
          </div>
          <Progress value={progressPct} className="h-2" />
        </CardContent>
      </Card>

      {/* ============== WP Connection ============== */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-500" />
              <h2 className="text-base font-semibold text-slate-900">WordPress</h2>
              {wpStatus.tested && (
                <Badge className={wpStatus.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                  {wpStatus.ok ? `✓ ${wpStatus.user}` : "✗ Connection failed"}
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setShowCreds((s) => !s)}
                data-testid="commercial-toggle-creds"
              >
                <Lock className="w-4 h-4 mr-1" /> {showCreds ? "Cacher" : "Éditer"} credentials
              </Button>
              <Button
                size="sm"
                onClick={testWp}
                disabled={busy["wp-test"]}
                data-testid="commercial-wp-test"
              >
                {busy["wp-test"] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Tester la connexion
              </Button>
            </div>
          </div>
          <div className="text-xs text-slate-500 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div><span className="font-semibold text-slate-700">URL:</span> {wpSettings?.wp_site_url || <em>non configuré</em>}</div>
            <div><span className="font-semibold text-slate-700">User:</span> {wpSettings?.wp_username || <em>non configuré</em>}</div>
            <div><span className="font-semibold text-slate-700">Password:</span> {wpSettings?.wp_app_password_set ? <span className="font-mono">{wpSettings.wp_app_password_masked}</span> : <em>non configuré</em>}</div>
          </div>

          {showCreds && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid="commercial-creds-form">
              <div>
                <Label htmlFor="wp_site_url" className="text-xs">URL WordPress (https://…)</Label>
                <Input
                  id="wp_site_url" data-testid="wp-url-input"
                  value={credsDraft.wp_site_url}
                  onChange={(e) => setCredsDraft({ ...credsDraft, wp_site_url: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="wp_username" className="text-xs">Username</Label>
                <Input
                  id="wp_username" data-testid="wp-user-input"
                  value={credsDraft.wp_username}
                  onChange={(e) => setCredsDraft({ ...credsDraft, wp_username: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="wp_pwd" className="text-xs">Application Password (laisser vide pour ne pas changer)</Label>
                <Input
                  id="wp_pwd" type="password" data-testid="wp-pwd-input"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  value={credsDraft.wp_app_password}
                  onChange={(e) => setCredsDraft({ ...credsDraft, wp_app_password: e.target.value })}
                />
              </div>
              <Button onClick={saveWpSettings} disabled={busy["wp-save"]} data-testid="wp-save-btn">
                {busy["wp-save"] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Sauvegarder
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============== Image Manager ============== */}
      <ImageManagerCard siteId={site.id} />

      {/* ============== Sitemap / SEO ============== */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Globe className="w-4 h-4 text-emerald-600" /> Sitemap & Google Search Console
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {totalPublished} URLs disponibles · RankMath publie un sitemap natif sur ton domaine.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`https://${site.domain_name}/sitemap_index.xml`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50"
                data-testid="sitemap-rankmath-link"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> Sitemap RankMath
              </a>
              <a
                href={`${API}/commercial/sites/${site.id}/sitemap.xml`}
                target="_blank" rel="noopener noreferrer"
                download="astar-sitemap.xml"
                className="inline-flex items-center text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50"
                data-testid="sitemap-download"
              >
                <Eye className="w-4 h-4 mr-1" /> Sitemap A-Star (preview/download)
              </a>
              <a
                href="https://search.google.com/search-console/sitemaps"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center text-sm px-3 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="sitemap-gsc-link"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> Ouvrir Google Search Console
              </a>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500 bg-emerald-50 border border-emerald-200 rounded p-3 leading-relaxed">
            <strong className="text-emerald-800">Recommandé pour GSC :</strong>{" "}
            soumettre <code className="bg-white px-1 rounded">sitemap_index.xml</code> (RankMath) car
            il est hébergé sur le même domaine que le site (requis par Google).
          </div>
        </CardContent>
      </Card>

      {/* ============== Dry-run toggle ============== */}
      <div className="flex items-center justify-end gap-3 text-sm">
        <Label htmlFor="dry-run" className="text-slate-600">Mode dry-run (aucun push live)</Label>
        <Switch
          id="dry-run" checked={dryRun} onCheckedChange={setDryRun}
          data-testid="dry-run-toggle"
        />
      </div>

      {/* ============== Batches ============== */}
      <div className="space-y-4">
        {plan.batches.map((b) => {
          const statusBadge = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
          const pct = Math.round((b.published_count / Math.max(b.target_count, 1)) * 100);
          return (
            <Card key={b.batch_number} data-testid={`batch-card-${b.batch_number}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">
                        Batch {b.batch_number}
                      </span>
                      <Badge className={statusBadge}>{b.status}</Badge>
                      {b.states_to_activate?.length > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          + activate: {b.states_to_activate.join(", ")}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{b.name}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{b.description}</p>
                    <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span><strong className="text-slate-700">{b.published_count}</strong> / {b.target_count} pages publiées</span>
                      {b.published_at && <span>Dernier push : {fmtDate(b.published_at)}</span>}
                    </div>
                    <div className="mt-2">
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-44">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => previewBatch(b.batch_number)}
                      disabled={busy[`prev-${b.batch_number}`]}
                      data-testid={`batch-preview-${b.batch_number}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> Preview
                    </Button>
                    {b.states_to_activate?.length > 0 && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => activateStates(b.batch_number)}
                        disabled={busy[`st-${b.batch_number}`]}
                        data-testid={`batch-activate-${b.batch_number}`}
                      >
                        Activer états
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => generateAI(b.batch_number)}
                      disabled={busy[`ai-${b.batch_number}`]}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`batch-ai-${b.batch_number}`}
                    >
                      {busy[`ai-${b.batch_number}`]
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <Sparkles className="w-4 h-4 mr-1" />}
                      Generate AI
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => publishBatch(b.batch_number)}
                      disabled={busy[`pub-${b.batch_number}`]}
                      className={dryRun ? "bg-slate-700 hover:bg-slate-800" : "bg-emerald-600 hover:bg-emerald-700"}
                      data-testid={`batch-publish-${b.batch_number}`}
                    >
                      {busy[`pub-${b.batch_number}`]
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <Upload className="w-4 h-4 mr-1" />}
                      {dryRun ? "Dry-run" : "Publish to WP"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ============== Activity log ============== */}
      {log.length > 0 && (
        <Card data-testid="commercial-activity-log">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Activité récente</h3>
            <ul className="space-y-1.5 text-xs font-mono">
              {log.map((l, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400 shrink-0">{l.at}</span>
                  <LogIcon kind={l.kind} />
                  <span className={
                    l.kind === "err" ? "text-red-700" :
                    l.kind === "warn" ? "text-amber-700" :
                    l.kind === "ok" ? "text-emerald-700" :
                    "text-slate-600"
                  }>{l.msg}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const LogIcon = ({ kind }) => {
  if (kind === "err") return <AlertCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />;
  if (kind === "warn") return <AlertCircle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />;
  if (kind === "ok") return <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />;
  return <span className="w-3 h-3 mt-0.5 shrink-0 text-slate-400">·</span>;
};
